import { useState, useRef } from "react";
import { safeTrace } from "../../../../lib/printer/giftCardPrintType";
import { message } from "antd";
import { useTranslation } from "react-i18next";
import { v4 as uuidv4 } from "uuid";
import dayjs from "dayjs";
import { getOAuthHeaders } from "../../../../constants/oAuthValidation";
import { timeStamp } from "../../../../utils/clock";
import db from "../../../../database";
import { verifyReturn, syncSingleProductByValue, cartHistory } from "../../../../services";
import usePOSConfigStore from "../../../../stores/usePOSConfigStore";
import useTransactionStore from "../../../../stores/useTransactionStore";
import useRestaurantStore from "../../../../stores/useRestaurantStore";
import { getIsInterState } from "../../../../utils/taxUtils";
import { groupItemsByTaxRate } from "../utils/taxCalculations";
import { roundTo, applyCurrencyRounding, getRoundingMethod, getPrecision } from "../../../../utils/tillHelpers";
import { trace } from "../../../../utils/debugTrace";

// BUG-2300: verifyReturn's line items carry a csUomId but no stdprecision/decimal info of
// their own, so qty math on them (qty - returnQty) has nothing to round against and leaks
// raw binary floating-point noise (e.g. 10.232 - 10.1 = 0.13199999999999967) into the UI.
// The precision lives in the local productUom table, keyed by the same csUomId — same
// lookup shape already used for receipt line items in salesHistoryUtils.js.
const getUomPrecisionMap = async () => {
  const allUoms = await db.productUom.toArray();
  const uomPrecisionMap = new Map();
  for (const uom of allUoms) {
    if (!uomPrecisionMap.has(uom.csUomId)) {
      uomPrecisionMap.set(uom.csUomId, uom.stdprecision ?? 2);
    }
  }
  return uomPrecisionMap;
};

const useSalesReturn = ({ cart, setCart, tillData, precision, posConfig, isExchange, loading, setLoading, setKeyValue, giftCardItems, setSideMenuDrawervisible }) => {
  const { t } = useTranslation();

  // Synchronous double-submit guard for searchReturnHistory — see the note at
  // its definition. The `loading` prop cannot serve this purpose.
  const searchReturnInFlightRef = useRef(false);

  // Return order state
  const [selectedReturnOrder, setSelectedReturnOrder] = useState({});
  const [dataSource, setDataSource] = useState([]);
  const [checkedKeys, setCheckedKeys] = useState([]);
  const [retrunSearchType, setReturnSearchType] = useState("orderNo");
  const [showReturnPurchase, setShowReturnPurchase] = useState(false);
  const [showReturnOptions, setShowReturnOptions] = useState(false);
  const [isBlindReceiptPending, setIsBlindReceiptPending] = useState(false);
  const [isPurchaseReturn, setIsPurchaseReturn] = useState(false);
  const selectedReturnType = useTransactionStore((s) => (s.selectedReturnType && s.selectedReturnType !== "null" ? s.selectedReturnType : null));
  const setSelectedReturnType = useTransactionStore((s) => s.setSelectedReturnType);
  const [displayReturnOrderSearch, setDisplayReturnOrderSearch] = useState(false);

  const returnProcessRef = useRef();
  const returnCartInFlightRef = useRef(false);

  // Internal index counter for selectPurchaseBillForReturn (mirrors the let index = 0 pattern)
  let selectIndex = 0;

  const handleSalesReturnFromSideMenu = () => {
    setSideMenuDrawervisible(false);
    setDisplayReturnOrderSearch(true);
  };

  const selectPurchaseBillForReturn = async (data) => {
    if (selectIndex === 0) {
      let returnBillItem = data[0];

      // DI-24: Return idempotency — check local DB for existing returns against this receipt
      // try {
      //   const originalOrderId = returnBillItem.sOrderID;
      //   if (originalOrderId) {
      //     const allLocalOrders = await db.orders.toArray();
      //     const alreadyReturned = allLocalOrders.filter((o) => (o.isReturn === true || o.isReturn === "Y") && o.items?.some((item) => item.sOrderReturnId === originalOrderId));
      //     if (alreadyReturned.length > 0) {
      //       message.error("This receipt has already been returned. Duplicate returns are not allowed.");
      //       console.error("[DI-24] Duplicate return blocked", {
      //         originalOrderId,
      //         existingReturnOrderIds: alreadyReturned.map((o) => o.sOrderID),
      //       });
      //       return;
      //     }
      //   }
      // } catch (err) {
      //   // Non-blocking — log but allow flow to continue in case of DB read failure
      //   console.error("[DI-24] Error checking return idempotency:", err);
      // }

      // Stage 1 — Build a per-line map of quantities already pending in parked returns for
      // this original order. Only block entirely if EVERY line is exhausted by parked qty.
      // For partial parked returns (e.g. 1 of 5 items parked), the remaining lines are
      // allowed through; parked qty is deducted from updatedDataSource further below.
      let parkedReturnQtyMap = {}; // sOrderlineID → total qty pending in parked returns
      if (posConfig?.saveParkedBills === "Y" && returnBillItem?.sOrderID) {
        try {
          const authHeadersCheck = getOAuthHeaders();
          if (authHeadersCheck && authHeadersCheck.access_token) {
            const today = dayjs();
            const filterBy = JSON.stringify({
              status: ["DR"],
              DateRange: [today.startOf("month").format("YYYY-MM-DD"), today.endOf("month").format("YYYY-MM-DD")],
            });
            const cartHistoryResponse = await cartHistory({
              q: "",
              filter_by: filterBy,
              sort_by: JSON.stringify({ created: "DESC" }),
              tillId: tillData?.tillAccess?.cwrTill?.cwrTillID || null,
            });
            if (cartHistoryResponse?.status === 200) {
              const parkedCarts = JSON.parse(cartHistoryResponse?.data?.data?.cartHistory || "[]");
              for (const parkedCart of parkedCarts) {
                const cartItems = parkedCart?.cart_items;
                if (!(cartItems?.isReturn === true || cartItems?.isReturn === "Y")) continue;
                for (const ci of cartItems?.items || []) {
                  if (ci.sOrderReturnId === returnBillItem.sOrderID && ci.sOrderlineReturnId) {
                    parkedReturnQtyMap[ci.sOrderlineReturnId] = (parkedReturnQtyMap[ci.sOrderlineReturnId] || 0) + Math.abs(ci.weight || 0);
                  }
                }
              }
              if (Object.keys(parkedReturnQtyMap).length > 0) {
                const allLinesExhausted = returnBillItem.line.every((line) => {
                  const returnable = line.returnQty > 0 ? line.qty - line.returnQty : line.qty;
                  return returnable - (parkedReturnQtyMap[line.sOrderlineID] || 0) <= 0;
                });
                if (allLinesExhausted) {
                  message.error("All items in this order are already pending in a parked return. Please retrieve or discard the parked return first.");
                  return;
                }
              }
            }
          }
        } catch (err) {
          console.error("[Stage1-DuplicateReturn] Error checking server parked returns:", err);
          parkedReturnQtyMap = {};
        }
      }

      setDisplayReturnOrderSearch(false);
      setReturnSearchType("orderNo");

      const discountValue = returnBillItem.discAmount;
      let grossstdTotal = 0;
      for (let i = 0; i < returnBillItem.line.length; i += 1) {
        grossstdTotal += parseFloat(returnBillItem.line[i].grossstd);
      }
      const zeroDiscountCount = returnBillItem.line.filter((item) => item.discount === 0).length;

      if (zeroDiscountCount === returnBillItem.line.length && discountValue > 0) {
        returnBillItem.line.forEach((addedToCart, index) => {
          const discountAmt = parseFloat((addedToCart.grossstd / grossstdTotal) * discountValue) / addedToCart.qty;
          const sp = parseFloat(addedToCart.grossstd) - discountAmt;
          addedToCart.grossstd = sp;
          addedToCart.linestd = sp * addedToCart?.weight;
          returnBillItem.line[index] = addedToCart;
        });
      }
      const updatedDataSource = returnBillItem.line.map((item) => ({ ...item }));
      const uomPrecisionMap = await getUomPrecisionMap();
      let itemsToReturn = 0;
      let itemsQtyToReturn = 0;
      let returnAmountTotal = 0;
      let itemsQtyPrecision = 2;

      updatedDataSource.forEach((_, i) => {
        updatedDataSource[i].key = `${i}`;
        const qtyPrecision = uomPrecisionMap.get(updatedDataSource[i].uom?.csUomId) ?? 2;
        updatedDataSource[i].qtyPrecision = qtyPrecision;
        updatedDataSource[i].remainingQty =
          updatedDataSource[i].returnQty > 0 ? roundTo(updatedDataSource[i].qty - updatedDataSource[i].returnQty, qtyPrecision) : updatedDataSource[i].qty;
        updatedDataSource[i].qtyToReturn =
          updatedDataSource[i].returnQty > 0 ? roundTo(updatedDataSource[i].qty - updatedDataSource[i].returnQty, qtyPrecision) : updatedDataSource[i].qty;
        itemsToReturn += 1;
        itemsQtyToReturn += updatedDataSource[i].qtyToReturn > 0 ? updatedDataSource[i].qtyToReturn : updatedDataSource[i].qty;
        if (updatedDataSource[i].qtyToReturn > 0) {
          itemsQtyPrecision = Math.max(itemsQtyPrecision, qtyPrecision);
        }
        if (updatedDataSource[i].linegross && updatedDataSource[i].qty) {
          const unitGross = Math.abs(updatedDataSource[i].linegross) / Math.abs(updatedDataSource[i].qty);
          returnAmountTotal += updatedDataSource[i].qtyToReturn * unitGross;
        } else {
          // Fallback to old calculation (only runs if return data is missing fields)
          returnAmountTotal += updatedDataSource[i].qtyToReturn * (Number(updatedDataSource[i].grossstd) || 0);
        }
      });
      updatedDataSource.itemsToReturn = 0;
      updatedDataSource.itemsQtyToReturn = 0;
      updatedDataSource.returnAmountTotal = 0;

      returnBillItem.itemsToReturn = itemsToReturn;
      returnBillItem.itemsQtyToReturn = itemsQtyToReturn;
      returnBillItem.returnAmountTotal = returnAmountTotal;
      returnBillItem.itemsQtyPrecision = itemsQtyPrecision;

      // Deduct quantities that are already pending in parked returns, then recompute summary.
      // Lines where parkedQty >= qtyToReturn get qtyToReturn clamped to 0 (hidden from UI).
      if (Object.keys(parkedReturnQtyMap).length > 0) {
        let adjItemsToReturn = 0;
        let adjItemsQtyToReturn = 0;
        let adjReturnAmountTotal = 0;
        let adjItemsQtyPrecision = 2;
        for (let i = 0; i < updatedDataSource.length; i++) {
          const parkedQty = parkedReturnQtyMap[updatedDataSource[i].sOrderlineID] || 0;
          const qtyPrecision = updatedDataSource[i].qtyPrecision ?? 2;
          if (parkedQty > 0) {
            updatedDataSource[i].qtyToReturn = roundTo(Math.max(0, updatedDataSource[i].qtyToReturn - parkedQty), qtyPrecision);
            updatedDataSource[i].remainingQty = roundTo(Math.max(0, updatedDataSource[i].remainingQty - parkedQty), qtyPrecision);
          }
          if (updatedDataSource[i].qtyToReturn > 0) {
            adjItemsToReturn += 1;
            adjItemsQtyToReturn += updatedDataSource[i].qtyToReturn;
            adjItemsQtyPrecision = Math.max(adjItemsQtyPrecision, qtyPrecision);
            if (updatedDataSource[i].linegross && updatedDataSource[i].qty) {
              const unitGross = Math.abs(updatedDataSource[i].linegross) / Math.abs(updatedDataSource[i].qty);
              adjReturnAmountTotal += updatedDataSource[i].qtyToReturn * unitGross;
            } else {
              adjReturnAmountTotal += updatedDataSource[i].qtyToReturn * (Number(updatedDataSource[i].grossstd) || 0);
            }
          }
        }
        returnBillItem.itemsToReturn = adjItemsToReturn;
        returnBillItem.itemsQtyToReturn = adjItemsQtyToReturn;
        returnBillItem.returnAmountTotal = adjReturnAmountTotal;
        returnBillItem.itemsQtyPrecision = adjItemsQtyPrecision;
      }

      setDataSource([...updatedDataSource]);
      // `updatedDataSource` carries the row `key` (and parked-qty adjustments) that the return
      // table and the activeKeys selection depend on. Since commit 8aea7958 changed the shallow
      // copy `[...returnBillItem.line]` to a per-item spread, `returnBillItem.line` no longer
      // shares those objects, so selectedReturnOrder.line had `key: undefined` and
      // addSelectedReturnProductsToCart's `activeKeys.includes(item.key)` never matched →
      // "No matching products". Point selectedReturnOrder.line at the keyed/adjusted rows.
      setSelectedReturnOrder({ ...returnBillItem, line: updatedDataSource });
      selectIndex++;
    }
  };

  const searchReturnHistory = async (documentno, record) => {
    // `loading` is a prop and setLoading is the parent's setState — neither
    // updates synchronously, so a state-only check cannot stop two same-tick
    // invocations. The ref is set alongside setLoading(true) inside the try
    // (not at the guard) so an early exit on missing auth headers can never
    // leave it stuck true.
    if (searchReturnInFlightRef.current || loading) return;
    const searchQuery = `${retrunSearchType} : "${documentno}"`;
    const authHeaders = getOAuthHeaders();
    if (authHeaders && authHeaders.access_token) {
      try {
        searchReturnInFlightRef.current = true;
        setLoading(true);
        const data = {
          query: `query {
                    verifyReturn(${searchQuery}) {
                        sOrderID
                        created
                        createdby
                        updated
                        updatedby
                        accumulation
                        redemption
                        documentno
                        salesRepID
                        dateordered
                        totalQty
                        orderTime
                        cwrProductQty
                        taxamt
                        grosstotal
                        discAmount
                        csBUnit {
                            csBunitId
                            name
                        }
                        cwrB2cCustomer {
                            cwrCustomerId
                            code
                            name
                            mobileNo
                            creditLimit
                            iscredit
                            creditBalance
                            creditApproachPercentage
                            pincode
                            email
                            retlLoyaltyBalance
                            loyaltyLevel{
                              cwrLoyaltyLevelId
                              name
                              accumulationRate
                              redemptionRate
                          }
                          b2cCustomerSegment{
                            name
                            cwrB2CCustomerSegmentId
                             }
                            sCustomer {
                                sCustomerID
                                customerCategory {
                                    sCustomerCateforyId
                                    value
                                    name
                                    description
                                }
                            }
                            cwrWallet {
                              balance_Amt
                              wallet_Id
                            }
                            metaData {
                                key
                                value
                            }
                        }
                        saleType {
                            cwrSaletypeId
                            name
                            value
                        }
                        cwrTill {
                            cwrTillID
                            till
                        }
                        finReceiptPlan {
                            finReceiptPlanDetails {
                                amount
                                cwrPaymentmethod {
                                    cWRPaymentMethodID
                                    finFinancialAccountId
                                    finPaymentmethodId
                                    integratedPayment
                                    isloyalty
                                    paymentProvider
                                }
                            }
                        }
                        line {
                            sOrderlineID
                            sOrderId
                            line
                            description
                            qty
                            netlist
                            netunit
                            linetax
                            unittax
                            linenet
                            linegross
                            grosslist
                            grossstd
                            returnline
                            returnQty
                            discount
                            mBatchId
                            batchNo
                            product {
                                mProductId
                                name
                                value
                                upc
                                hsncode
                                imageurl
                                isManualQty
                                shortDescription
                                returnable
                                returnDays
                            }
                            uom {
                                csUomId
                                name
                            }
                            tax {
                                csTaxID
                                name
                                rate
                            }
                            pricingRule {
                              mPricingrulesId
                              name
                              discount
                            }
                        }
                        metaData {
                            key
                            value
                        }
                    }
                }`,
        };
        const searchData = await verifyReturn(data);
        let matchedRecord = searchData.data.data.verifyReturn.filter((item) => item.sOrderID === record.sOrderID);
        await selectPurchaseBillForReturn(matchedRecord);
        setShowReturnPurchase(true);
        return matchedRecord[0];
      } catch (error) {
        console.error(error);
      } finally {
        searchReturnInFlightRef.current = false;
        setLoading(false);
      }
    }
  };

  const getProductData = (data, itemData) => {
    return new Promise(function (resolve) {
      db.products
        .where("mProductId")
        .equalsIgnoreCase(data)
        .toArray()
        .then((allProducts) => allProducts.filter((p) => p.isactive == true && p.productSegment !== "BG"))
        .then(async (product) => {
          // When the product isn't in IndexedDB (out-of-stock items are skipped during sync),
          // fetch it from the server by SKU value and save to IndexedDB before proceeding.
          if (product.length === 0 && itemData?.product?.value) {
            try {
              const syncId = uuidv4().replace(/-/g, "").toUpperCase();
              const tillId = tillData?.tillAccess?.cwrTill?.cwrTillID;
              await syncSingleProductByValue(tillData, syncId, tillId, itemData.product.value, { ignoreLastSync: true });
              const refetched = await db.products.where("mProductId").equalsIgnoreCase(data).toArray();
              product = refetched.filter((p) => p.isactive == true && p.productSegment !== "BG");
            } catch (err) {
              console.error("syncSingleProductByValue fallback failed:", err);
            }
          }
          if (product.length > 0) {
            const obj = { ...product[0] };
            if (obj.batchedProduct === "Y") {
              trace("useSalesReturn", "getProductData: batch data available for return line", {
                mProductId: obj.mProductId,
                batchedProduct: obj.batchedProduct,
                batchedForSale: obj.batchedForSale,
                batchedForStock: obj.batchedForStock,
                localMBatch: Array.isArray(obj.mBatch) ? obj.mBatch.map((b) => ({ batchno: b.batchno, mBatchId: b.mBatchId })) : obj.mBatch,
                originalLineBatchNo: itemData?.batchNo,
                originalLineMBatchId: itemData?.mBatchId,
              });
            }
            if (obj.overRideTax === "Y" && obj.sunitprice <= obj.overRideCondition) {
              // prettier-ignore
              const originalPrice = obj.sunitprice - (obj.sunitprice - (obj.sunitprice * (100 / (100 + obj.taxRate))));
              const taxedPrice = originalPrice + (originalPrice * obj.contraRate) / 100;
              obj.sunitprice = taxedPrice;
              obj.cTaxId = obj.contraTaxId;
              obj.taxRate = obj.contraRate;
            }
            const productDefined = {
              batchno: null,
              description: obj.description,
              discount: itemData.discount * -1,
              productSegment: obj?.productSegment || "",
              imageurl: obj.imageurl,
              isDecimal: obj.isDecimal,
              isManualQty: obj.isManualQty,
              restrictPosQtyEdit: obj.restrictPosQtyEdit || "N",
              showAlternateUomInPos: obj?.showAlternateUomInPos || "N",
              interTaxId: obj?.interTaxId || "",
              interTaxRate: obj?.interTaxRate || 0,
              interTaxName: obj?.interTaxName || "",
              alternateUom: obj?.alternateUom || null,
              isPromoApplicable: false,
              isReturn: true,
              mBatchId: null,
              mPricingruleId: null,
              name: obj.name,
              name2: obj.name2,
              nettotal: itemData.linegross * -1,
              primaryOrderLine: null,
              productId: obj.mProductId,
              realPrice: obj.sunitprice * -1,
              listPrice: obj.slistprice * -1,
              sunitprice: obj.sunitprice * -1,
              returnQty: null,
              salePrice: itemData.grossstd * -1,
              unitTax: itemData.unittax * -1,
              mrpPrice: obj.sunitprice * -1,
              stock: obj.onhandQty,
              taxCategory: obj.taxCategory,
              taxName: obj?.taxName,
              taxFlag: obj?.taxFlag || "N",
              tax: obj.cTaxId,
              taxAmount: itemData.linetax * -1,
              taxRate: obj.taxRate,
              uom: obj.csUomId,
              uom_name: obj.uomName,
              isDecimalQty: obj.uomData?.length > 0 ? (obj.uomData[0].decimal === "Y" ? true : false) : false,
              isQtyDesimal: obj.uomData?.[0]?.stdprecision ?? 2,
              upc: obj.upc,
              value: obj.value,
              weight: 0,
              shortDescription: obj.shortDescription,
              hsncode: obj.hsncode,
              csBunitId: obj.csBunitId,
              mProductCategoryId: obj.mProductCategoryId,
              productManufacturerId: obj.productManufacturerId,
              productBrandId: obj.productBrandId,
              productBrandName: obj?.productBrandName,
              batchedProduct: obj.batchedProduct,
              batchedForSale: obj.batchedForSale,
              batchedForStock: obj.batchedForStock,
              multiPrice: obj.multiPrice,
              shelfLife: obj.shelfLife,
              discountName: itemData.pricingRule.name,
              mPricingruleId: itemData.pricingRule.mPricingrulesId,
            };
            resolve(productDefined);
          } else {
            {
              message.warning(`${t("product_search_error")}`);
            }
            resolve(null);
          }
        });
    });
  };

  const handleSaveReturnCartRecord = (data) => {
    let itemsToReturn = 0;
    let itemsQtyToReturn = 0;
    let returnAmountTotal = 0;
    let itemsQtyPrecision = 2;
    data.forEach((item, i) => {
      if (item.qtyToReturn > 0) {
        itemsToReturn += 1;
        itemsQtyToReturn += item.qtyToReturn;
        returnAmountTotal += item.qtyToReturn * item.grossstd;
        itemsQtyPrecision = Math.max(itemsQtyPrecision, item.qtyPrecision ?? 2);
      }
    });

    selectedReturnOrder.line = data;
    selectedReturnOrder.itemsToReturn = itemsToReturn;
    selectedReturnOrder.itemsQtyToReturn = itemsQtyToReturn;
    selectedReturnOrder.returnAmountTotal = returnAmountTotal;
    selectedReturnOrder.itemsQtyPrecision = itemsQtyPrecision;
    setSelectedReturnOrder({ ...selectedReturnOrder });
  };

  const posLogActivity = (record, activity) => {
    const timeMark = timeStamp();
    const currentDate = new Date().toLocaleDateString("zh-Hans-CN");
    let products = "";

    const trxId = uuidv4().replace(/-/g, "").toUpperCase();

    if (Array.isArray(record)) {
      record.forEach((item) => {
        products += `SKU: ${item.value}, Qty: ${item.weight}, Unit Price: ${item.salePrice}, `;
      });
    } else {
      products = `SKU: ${record.value}, Qty: ${record.weight}, Unit Price: ${record.salePrice}, `;
    }

    db.logInformation.add({
      type: activity,
      action: "LOG",
      description: activity === "SLR" ? products : "",
      date: currentDate,
      time: timeMark,
      orderNo: `${cart.documentno}`,
      remarks: "",
      transactionId: `${trxId}`,
      status: "SCS",
      duration: null,
    });
  };

  const addSelectedReturnProductsToCart = async (keysOverride) => {
    if (returnCartInFlightRef.current) return;
    returnCartInFlightRef.current = true;
    try {
      const activeKeys = keysOverride || checkedKeys;
      if (!selectedReturnOrder?.line?.length) return;

      // DI-25: Pre-validate return amount before mutating cart
      {
        // BUG-1825: selectedReturnOrder.grosstotal is the backend's ROUNDED order total
        // (post round-off), but estimatedReturnTotal below sums UNROUNDED per-line amounts.
        // Round-off is a whole-invoice adjustment never distributed back to lines, so a full
        // return of an order with a non-zero round-off would always be a few paise "over" the
        // rounded header total. Compare against the same unrounded per-line basis instead of
        // the rounded header total so both sides are apples-to-apples — this needs no fudge
        // tolerance beyond ordinary floating-point noise.
        const originalTotal = selectedReturnOrder.line.reduce((sum, item) => {
          if (item.linegross) return sum + Math.abs(item.linegross);
          return sum + Math.abs(Number(item.netamt) || Number(item.linetotal) || 0);
        }, 0);
        if (originalTotal > 0) {
          const estimatedReturnTotal = selectedReturnOrder.line
            .filter((item) => activeKeys.includes(item.key) && item.qtyToReturn > 0)
            .reduce((sum, item) => {
              if (item.linegross) {
                const unitGross = Math.abs(item.linegross) / Math.abs(item.qty);
                return sum + unitGross * item.qtyToReturn;
              }
              return sum + Math.abs(Number(item.netamt) || Number(item.linetotal) || 0) * (item.qtyToReturn / Math.abs(item.qty || 1));
            }, 0);
          if (estimatedReturnTotal > originalTotal + 0.01) {
            console.error("[DI-25] Return amount exceeds original order total", {
              estimatedReturnTotal,
              originalTotal,
              difference: estimatedReturnTotal - originalTotal,
            });
            message.error("Return amount cannot exceed the original order amount.");
            setLoading(false);
            return;
          }
        }
      }

      let cartItems = [...cart.items];
      for (let index = 0; index < selectedReturnOrder.line.length; index++) {
        const item = { ...selectedReturnOrder.line[index] };
        let giftCardItem = giftCardItems.filter((giftCard) => giftCard.productId === item.product.mProductId);
        if (giftCardItem.length > 0) {
          safeTrace("return: line is a gift card / voucher / credit note product (not re-added as a normal product line)", { productId: item.product?.mProductId || null, name: item.product?.name || null, qtyToReturn: item.qtyToReturn ?? null });
        }
        const productItem = activeKeys.includes(item.key) && giftCardItem.length === 0 ? await getProductData(item.product.mProductId, item) : null;
        if (productItem && item.qtyToReturn > 0) {
          productItem.sOrderReturnId = item.sOrderId;
          productItem.sOrderlineReturnId = item.sOrderlineID;
          productItem.wastage = (useRestaurantStore.getState().wastageflag || localStorage.getItem("wastageFlag")) === "Y" ? "Y" : "N";
          productItem.batchno = item?.batchNo?.split("-:")[0] || item?.batchNo || null;
          productItem.mBatchId = item?.mBatchId || null;
          if (item.returnQty < item.qty) {
            let discountAmount = productItem.discount / item.qty;
            productItem.weight = -item.qtyToReturn;
            const mrp = parseFloat(Math.abs(productItem.salePrice)) * parseFloat(item.qtyToReturn);
            const tax = productItem.taxAmount / item.qty;
            productItem.discount = discountAmount * item.qtyToReturn;
            productItem.taxAmount = tax * item.qtyToReturn;
            // Return receipt Rate/Amount mismatch: item.grossstd is the product's current
            // standard/catalog price, not what was actually charged on this line — it drifts
            // from the sale-time price whenever the
            // catalog rate changes (e.g. daily-rate loose items), so a return printed with grossstd
            // shows a different Rate/Amount than the original sale receipt. linegross is the actual
            // per-line amount charged, so its derived unit price must take priority — same basis
            // already used for nettotal below.
            const unitGross = item.linegross ? Math.abs(item.linegross) / Math.abs(item.qty) : 0;
            productItem.realPrice = unitGross > 0 ? unitGross : item.grossstd > 0 ? item.grossstd : (item.netunit + item.linetax + item.discount) / item.qty;
            if (item.linegross) {
              productItem.nettotal = -(unitGross * item.qtyToReturn);
            } else {
              // fallback to old logic (normal sales)
              productItem.nettotal = mrp > 0 ? -Math.abs(mrp - parseFloat(productItem.discount ? Math.abs(productItem.discount) : 0)) : 0;
            }
            // This return path never set linestd, so useCoreEffects.js's cart-precision-guard
            // effect (which recomputes totalStd as sum(item.linestd) whenever cart.total drifts
            // from its rounded value) would silently re-zero the order-level totalStd fix below
            // for any return that trips that guard. Returns don't run the discount-pipeline
            // pricing-rule math that makes linestd (pre-discount) diverge from nettotal
            // (post-discount) elsewhere in the app, so linestd = nettotal here is correct, not
            // an approximation.
            productItem.linestd = productItem.nettotal;
            let netStd = parseFloat(Math.abs(productItem.sunitprice) - Math.abs(productItem.sunitprice) / (1 + Math.abs(productItem.taxRate) / 100));
            const saleTypeData = usePOSConfigStore.getState().saleTypeData || {};
            let taxIncludeFlag = saleTypeData?.customer?.istaxincluded === "Y" ? "Y" : tillData.tillAccess.csBunit.isTaxIncluded ? tillData.tillAccess.csBunit.isTaxIncluded : "Y";
            productItem.netStd =
              taxIncludeFlag === "Y"
                ? parseFloat(((Math.abs(productItem.sunitprice) - netStd) * -1).toFixed(tillData.tillAccess.csBunit.currencies[0].prcPrecision))
                : productItem.sunitprice;
            cartItems.push(productItem);
          }
          if (
            db.logConfiguration.toArray().then((fetched) => {
              fetched.map((item) => {
                item.salesReturn.filter((item) => item.log === "Y").length > 0;
              });
            })
          ) {
            posLogActivity(productItem, "SLR");
          }
        }
      }

      const returnItemsAdded = cartItems.length - cart.items.length;
      if (returnItemsAdded === 0) {
        message.warning("No matching products found in the system for the selected return items.");
        setLoading(false);
        return;
      }

      // DI-25 post-mutation safety check (primary validation is pre-loop)
      {
        const returnTotal = cartItems.filter((item) => item.isReturn === true).reduce((sum, item) => sum + Math.abs(Number(item.nettotal) || 0), 0);
        // Same unrounded per-line basis as the pre-loop check above — see BUG-1825 comment there.
        const originalTotal = selectedReturnOrder.line.reduce((sum, item) => {
          if (item.linegross) return sum + Math.abs(item.linegross);
          return sum + Math.abs(Number(item.netamt) || Number(item.linetotal) || 0);
        }, 0);
        if (originalTotal > 0 && returnTotal > originalTotal + 0.01) {
          console.error("[DI-25] Post-mutation return amount exceeds original", { returnTotal, originalTotal });
        }
      }

      const itemsSummery = {
        decimalItems: { totalQty: 0, totalAmount: 0 },
        nonDecimalItems: { totalQty: 0, totalAmount: 0 },
        itemsQty: 0,
        items: 0,
      };

      cartItems.map((item) => {
        if (Array.isArray(item.children) && item.children.length > 0) {
          item.children.forEach((child) => {
            const isDecimal = child.uomData?.[0]?.decimal === "Y" || child.isDecimal;
            const summaryKey = isDecimal ? "decimalItems" : "nonDecimalItems";

            itemsSummery.itemsQty += child.weight;
            itemsSummery.items += 1;
            itemsSummery[summaryKey].totalQty += child.weight;
            itemsSummery[summaryKey].totalAmount += parseFloat(parseFloat(child.nettotal)?.toFixed(precision));
          });
        } else {
          const isDecimal = item.uomData?.[0]?.decimal === "Y" || item.isDecimal;
          const summaryKey = isDecimal ? "decimalItems" : "nonDecimalItems";

          itemsSummery.itemsQty += item.weight;
          itemsSummery.items += 1;
          itemsSummery[summaryKey].totalQty += item.weight;
          itemsSummery[summaryKey].totalAmount += parseFloat(parseFloat(item.nettotal)?.toFixed(precision));
        }

        return item;
      });

      let orderTimeDetails = usePOSConfigStore.getState().orderTimeDetails || { orderStartTime: "", orderEndTime: "", paymentStartTime: "" };
      orderTimeDetails = {
        ...orderTimeDetails,
        paymentStartTime: "",
        orderStartTime: orderTimeDetails.orderStartTime !== "" ? orderTimeDetails.orderStartTime : dayjs(new Date()).format("DD-MM-YYYY HH:mm:ss"),
      };
      usePOSConfigStore.getState().setOrderTimeDetails(orderTimeDetails);

      let updatedCart = {
        ...cart,
        items: cartItems,
        isReturn: true,
        customer: selectedReturnOrder.cwrB2cCustomer,
      };
      const isInterState = getIsInterState(updatedCart);
      const groupedItemsByTaxRate = groupItemsByTaxRate(updatedCart.items || [], precision, { isInterState });
      let updatedTotalTax = 0;
      let updatedTotalPrice = 0;
      let updatedTotalItemsQty = 0;
      let updatedTotalDiscounts = 0;
      let updatedSubtotal = 0;
      for (let i = 0; i < updatedCart.items.length; i += 1) {
        updatedTotalPrice += updatedCart.items[i].nettotal;
        updatedSubtotal += updatedCart.items[i].nettotal;
        updatedTotalItemsQty += updatedCart.items[i].weight;
        updatedTotalTax += updatedCart.items[i].taxAmount;
        updatedTotalDiscounts += updatedCart.items[i].discount ? updatedCart.items[i].discount : 0;
        updatedCart.items[i].key = i;
      }
      const prcPrecision = tillData.tillAccess.csBunit.currencies[0].prcPrecision;
      let returnTcs = 0;
      const tcsMetaData = selectedReturnOrder?.metaData?.find((m) => m.key === "tcsDetails");
      if (tcsMetaData?.value) {
        try {
          const tcsDetails = JSON.parse(tcsMetaData.value);
          const originalTcsTotal = parseFloat(tcsDetails.total) || 0;
          if (originalTcsTotal > 0 && Array.isArray(tcsDetails.items) && tcsDetails.items.length > 0) {
            const originalOrderNettotal = tcsDetails.items.reduce((sum, it) => sum + (parseFloat(it.nettotal) || 0), 0);
            if (originalOrderNettotal > 0) {
              for (let i = 0; i < updatedCart.items.length; i += 1) {
                const returnedItem = updatedCart.items[i];
                const matchedTcs = tcsDetails.items.find((it) => it.productId === (returnedItem.productId || returnedItem.product?.mProductId));
                if (matchedTcs && parseFloat(matchedTcs.amount) > 0) {
                  const fullQty = parseFloat(matchedTcs.qty) || 1;
                  const tcsPerUnit = parseFloat(matchedTcs.amount) / fullQty;
                  const returnedQty = returnedItem.weight || returnedItem.qty || 1;
                  returnedItem.itemTcs = -parseFloat((tcsPerUnit * returnedQty).toFixed(prcPrecision));
                } else {
                  returnedItem.itemTcs = 0;
                }
                returnTcs += returnedItem.itemTcs;
              }
            }
          }
        } catch (e) {
          console.error("Error parsing tcsDetails for return:", e);
        }
      }
      returnTcs = parseFloat(returnTcs.toFixed(prcPrecision));

      // BUG-1833: a normal sale rounds its total via applyCurrencyRounding (cart.js) and shows
      // the difference as the "Round Off" line. This return path summed nettotal directly and
      // never applied that same rounding, so a return total like -169.05 stayed unrounded (no
      // Round Off line, mismatched vs. what a same-amount sale would show as -169.00).
      const preRoundTotal = parseFloat((updatedTotalPrice + returnTcs).toFixed(prcPrecision));
      const { roundedTotal: returnRoundedTotal, roundOffAmount: returnRoundOff } = applyCurrencyRounding(preRoundTotal, getRoundingMethod(tillData), getPrecision(tillData));

      let finalCartObj = {
        ...updatedCart,
        items: [...updatedCart.items],
        documentno: posConfig?.disableOfflineBilling === "Y" ? selectedReturnOrder?.documentno : updatedCart.documentno,
        total: returnRoundedTotal,
        // Some tills' custom receipt templates bind the printed "TOTAL" row to {{totalStd}}
        // instead of {{total}} (see CWHardwareController.js's identical overPayedAmount
        // adjustment applied to both fields). Normal sale flows (cart.js) always populate
        // totalStd; this return path never did, so it stayed at the cart's init value of 0
        // and every return receipt printed "TOTAL 0.00" regardless of the correct total.
        totalStd: returnRoundedTotal,
        roundOff: returnRoundOff,
        tax: roundTo(updatedTotalTax, getPrecision(tillData)),
        discount: updatedTotalDiscounts,
        totalQty: updatedTotalItemsQty,
        subTotal: updatedSubtotal - updatedTotalTax,
        tcs: returnTcs,
        groupedItemsByTaxRate,
        salesRepId: selectedReturnOrder?.salesRepID || null,
        itemsSummery,
        returnOriginalDocumentNo: selectedReturnOrder?.documentno || "",
      };
      setCart({ ...finalCartObj });
      setLoading(false);
      setKeyValue("2");
      return finalCartObj;
    } catch (error) {
      console.error("Error adding selected return products to cart:", error);
    } finally {
      returnCartInFlightRef.current = false;
    }
  };

  return {
    // State
    selectedReturnOrder,
    setSelectedReturnOrder,
    dataSource,
    setDataSource,
    checkedKeys,
    setCheckedKeys,
    retrunSearchType,
    setReturnSearchType,
    showReturnPurchase,
    setShowReturnPurchase,
    showReturnOptions,
    setShowReturnOptions,
    isBlindReceiptPending,
    setIsBlindReceiptPending,
    isPurchaseReturn,
    setIsPurchaseReturn,
    selectedReturnType,
    setSelectedReturnType,
    displayReturnOrderSearch,
    setDisplayReturnOrderSearch,
    returnProcessRef,
    // Handlers
    handleSalesReturnFromSideMenu,
    selectPurchaseBillForReturn,
    searchReturnHistory,
    getProductData,
    handleSaveReturnCartRecord,
    addSelectedReturnProductsToCart,
  };
};

export default useSalesReturn;
